import { FetchResult } from "@apollo/client";
import {
  Button,
  Chip,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  Tooltip,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { CsvUploader } from "src/components/CsvUploader";
import { CopyTakeoffLineItemsInput, SaveTakeoffLineItemInput } from "src/generated/graphqlTypes";
import { usePlanPackagesQuery } from "src/routes/libraries/plan-package/list/PlanPackagesPage.generated";
import { TakeoffsStore_CopyTakeoffLineItemsMutation } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext.generated";
import { fail, itemTemplateStatusToTagType } from "src/utils";
import { SaveTliResult } from "../TakeoffsManagerContext";
import { useTakeoffUploader } from "./useTakeoffUploader";

export type AddItemsFromExistingPlanModalProps = {
  planPackageId: string;
  planPackageVersionId: string;
  // We accept fns that know how to call GraphQL + update the cache + redirect to draft
  copyFromPlan: (input: CopyTakeoffLineItemsInput) => Promise<FetchResult<TakeoffsStore_CopyTakeoffLineItemsMutation>>;
  saveTli: (item: SaveTakeoffLineItemInput | SaveTakeoffLineItemInput[]) => SaveTliResult;
};

export function AddItemsFromExistingPlanModal(props: AddItemsFromExistingPlanModalProps) {
  const { planPackageId, planPackageVersionId, copyFromPlan, saveTli } = props;
  const tid = useTestIds(props);
  const { errors, addError, handleOnDrop, handleOnClick, importDataReady, importCounts } = useTakeoffUploader(
    saveTli,
    undefined,
    planPackageId,
    planPackageVersionId,
  );
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [sourcePpId, setSourcePpId] = useState<string>();
  const [usingCsv, setUsingCsv] = useState(false);
  const allPlanPackagesQuery = usePlanPackagesQuery({ variables: { filter: {} } });
  const pps = allPlanPackagesQuery.data?.planPackages.entities;
  const sourcePlanPackage = pps?.find((pp) => pp.id === sourcePpId);
  const allPlans = useMemo(
    () => pps?.filter((pp) => pp.id !== planPackageId).sortBy((pp) => pp.displayName) ?? [],
    [pps, planPackageId],
  );

  const maybeTriggerNotice = useCallback(
    (addedCount: number) => {
      if (sourcePlanPackage) {
        triggerNotice({
          message: `${addedCount} items were successfully added from ${sourcePlanPackage.displayName}`,
        });
      }
    },
    [sourcePlanPackage, triggerNotice],
  );

  const onClickHandler = useCallback(async () => {
    if (usingCsv) {
      await handleOnClick();
    } else if (sourcePlanPackage) {
      const result = await copyFromPlan({
        targetReadyPlanId: planPackageId,
        sourceReadyPlanId: sourcePlanPackage.id ?? fail("expected a ready plan"),
      });
      const items = result.data?.copyTakeoffLineItems?.copiedItems ?? [];
      maybeTriggerNotice(items.length);
    }
    closeModal();
  }, [usingCsv, sourcePlanPackage, closeModal, handleOnClick, copyFromPlan, planPackageId, maybeTriggerNotice]);

  const handleOnDropCsv = useCallback(
    async (...params: Parameters<typeof handleOnDrop>) => {
      setUsingCsv(true);
      setSourcePpId(undefined);
      await handleOnDrop(...params);
    },
    [setUsingCsv, handleOnDrop],
  );

  return (
    <>
      <ModalHeader>Import Items</ModalHeader>
      <ModalBody>
        <div css={Css.mt2.mb1.$}>
          <>
            <div css={Css.sm.gray800.$}>
              Import items from a <span css={Css.blue600.$}>.csv file</span> or from another plan
            </div>
            <label css={{ ...Css.gray700.sm.mt3.db.$, "& button": Css.bcBlue600.bw2.bgBlue50.$ }}>
              Upload Takeoffs
              <CsvUploader errors={errors} onError={addError} onDrop={handleOnDropCsv} />
            </label>
            {importCounts && (
              <div css={Css.mt2.mb1.$}>
                <div css={Css.sm.gray800.$}>
                  {importCounts.updates} items will be updated and {importCounts.new} items will be added
                </div>
              </div>
            )}
            <div css={Css.mt2.mb1.$}>
              <SelectField
                {...tid.selectPlanPackage}
                label="Import from another plan"
                value={sourcePpId}
                onSelect={setSourcePpId}
                options={allPlans}
                getOptionLabel={(pp) => pp.displayName}
                getOptionMenuLabel={(pp) => (
                  <div css={Css.df.jcc.aic.$}>
                    <span css={Css.wPx(310).mrPx(20).$}>{pp.displayName}</span>
                    <span>
                      <Tooltip title={pp.status.name} placement="right">
                        <Chip
                          text={pp.aggregateLatest.displayVersion}
                          type={itemTemplateStatusToTagType(pp.status.code)}
                        />
                      </Tooltip>
                    </span>
                  </div>
                )}
                getOptionValue={(pp) => pp.id}
                disabled={allPlanPackagesQuery.loading || usingCsv}
                placeholder={
                  usingCsv
                    ? "You are importing from a csv file"
                    : allPlanPackagesQuery.loading
                      ? "Loading plans..."
                      : "Select plan"
                }
              />
            </div>
          </>
        </div>
      </ModalBody>
      <ModalFooter>
        <div css={Css.df.gap1.$}>
          <Button label="Cancel" variant="quaternary" onClick={closeModal} />
          <Button
            label="Add Items"
            variant="primary"
            disabled={usingCsv ? !importDataReady || errors.nonEmpty || !importCounts : !sourcePpId}
            onClick={onClickHandler}
            {...tid.addItemsbutton}
          />
        </div>
      </ModalFooter>
    </>
  );
}
